import {z} from "zod";

/** Pre-paywall quiz answers. Every field is optional so partial progress validates. */
export const funnelAnswersSchema = z.object({
  goal: z.enum(["spending", "saving", "clarity", "bills"]).optional(),
  feeling: z.enum(["anxious", "avoid", "okay", "confident"]).optional(),
  spendGuess: z.enum(["under_1k", "1k_2k", "2k_4k", "4k_6k", "over_6k"]).optional(),
  leak: z.enum(["food", "shopping", "subscriptions", "transport", "fun", "no_idea"]).optional(),
  subscriptions: z.enum(["1_3", "4_6", "7_10", "10_plus", "no_idea"]).optional(),
  checkFrequency: z.enum(["daily", "weekly", "monthly", "rarely"]).optional(),
  accounts: z.enum(["one", "several", "cards"]).optional(),
  dream: z.enum(["travel", "safety", "debt", "invest", "home", "freedom"]).optional(),
  checkInTime: z.enum(["morning", "midday", "evening", "night"]).optional(),
  source: z.enum(["app-store", "meta", "tiktok-youtube", "search", "ai", "reddit", "friend", "other"]).optional()
});
export type FunnelAnswers = z.infer<typeof funnelAnswersSchema>;
export type FunnelQuestionId = keyof FunnelAnswers;

/** Quiz copy shared by the app funnel and the public money personality quiz. */
export interface FunnelChoice {
  value: string;
  title: string;
  detail?: string;
  emoji: string;
}
export interface FunnelQuestion {
  id: FunnelQuestionId;
  title: string;
  detail: string;
  options: FunnelChoice[];
  optional?: boolean;
  /** Shown after an answer, keyed by value or "*" for every answer. */
  reactions?: Partial<Record<string, string>>;
}

export const funnelQuestions: readonly FunnelQuestion[] = [
  {
    id: "goal",
    title: "What brings you to Sprout?",
    detail: "Pick the one that matters most right now.",
    options: [
      {value: "spending", title: "Know where my money goes", emoji: "🧭"},
      {value: "saving", title: "Save more every month", emoji: "🐷"},
      {value: "clarity", title: "See everything in one place", emoji: "🔭"},
      {value: "bills", title: "Stop sneaky bills & subscriptions", emoji: "🧾"}
    ],
    reactions: {"*": "Great pick. Sprout will build your plan around it."}
  },
  {
    id: "feeling",
    title: "How do you feel when you check your balance?",
    detail: "Be honest. Sprout doesn’t judge.",
    options: [
      {value: "anxious", title: "A little anxious", emoji: "😰"},
      {value: "avoid", title: "I try not to look", emoji: "🙈"},
      {value: "okay", title: "It’s fine, mostly", emoji: "😐"},
      {value: "confident", title: "Confident", emoji: "😎"}
    ],
    reactions: {anxious: "You’re not alone. One calm minute a day changes how this feels.", avoid: "Totally normal. Sprout makes looking painless: one minute, plain words.", okay: "Nice. Let’s turn “fine” into “in control”.", confident: "Love it. Sprout will help you stay sharp with less effort."}
  },
  {
    id: "spendGuess",
    title: "Roughly how much do you spend in a month?",
    detail: "Your best guess. We’ll check it against reality later 😉",
    options: [
      {value: "under_1k", title: "Under $1,000", emoji: "🌱"},
      {value: "1k_2k", title: "$1,000 – $2,000", emoji: "🌿"},
      {value: "2k_4k", title: "$2,000 – $4,000", emoji: "🪴"},
      {value: "4k_6k", title: "$4,000 – $6,000", emoji: "🌳"},
      {value: "over_6k", title: "More than $6,000", emoji: "🌲"}
    ],
    reactions: {"*": "Noted. Most guesses are off, so this will be fun."}
  },
  {
    id: "leak",
    title: "Where do you think your money leaks most?",
    detail: "Go with your gut.",
    options: [
      {value: "food", title: "Food & delivery", emoji: "🍔"},
      {value: "shopping", title: "Online shopping", emoji: "📦"},
      {value: "subscriptions", title: "Subscriptions", emoji: "📺"},
      {value: "transport", title: "Rides & gas", emoji: "🚗"},
      {value: "fun", title: "Going out", emoji: "🎉"},
      {value: "no_idea", title: "No idea, that’s the problem", emoji: "🤷"}
    ],
    reactions: {no_idea: "That’s exactly what Sprout is for.", "*": "Sprout will show you the real number, not just a hunch."}
  },
  {
    id: "subscriptions",
    title: "How many subscriptions do you pay for?",
    detail: "Streaming, apps, gym, cloud storage…",
    options: [
      {value: "1_3", title: "1 – 3", emoji: "📱"},
      {value: "4_6", title: "4 – 6", emoji: "📺"},
      {value: "7_10", title: "7 – 10", emoji: "🎧"},
      {value: "10_plus", title: "More than 10", emoji: "🎰"},
      {value: "no_idea", title: "Honestly, no idea", emoji: "🤔"}
    ],
    reactions: {"*": "People tend to underestimate this one. Sprout finds every recurring charge."}
  },
  {
    id: "checkFrequency",
    title: "How often do you check your spending?",
    detail: "No wrong answer.",
    options: [
      {value: "daily", title: "Every day", emoji: "📆"},
      {value: "weekly", title: "Every week or so", emoji: "🗓️"},
      {value: "monthly", title: "When the statement arrives", emoji: "✉️"},
      {value: "rarely", title: "Rarely", emoji: "🫣"}
    ],
    reactions: {"*": "Sprout turns this into a one-minute daily habit."}
  },
  {
    id: "accounts",
    title: "What does your money setup look like?",
    detail: "So we start with the right accounts.",
    options: [
      {value: "one", title: "One main bank", emoji: "🏦"},
      {value: "several", title: "A few different banks", emoji: "🏛️"},
      {value: "cards", title: "Banks and credit cards", emoji: "💳"}
    ]
  },
  {
    id: "dream",
    title: "What would you do with an extra $300 a month?",
    detail: "Your answer shapes your plan.",
    options: [
      {value: "travel", title: "Take that trip", emoji: "✈️"},
      {value: "safety", title: "Build a safety net", emoji: "🛟"},
      {value: "debt", title: "Pay off debt", emoji: "⛓️"},
      {value: "invest", title: "Invest more", emoji: "📈"},
      {value: "home", title: "Save for a home", emoji: "🏡"},
      {value: "freedom", title: "Just breathe easier", emoji: "🕊️"}
    ]
  },
  {
    id: "checkInTime",
    title: "When should Sprout check in with you?",
    detail: "One minute a day. You can change it anytime.",
    options: [
      {value: "morning", title: "With my morning coffee", detail: "8:00 am", emoji: "☀️"},
      {value: "midday", title: "On my lunch break", detail: "12:00 pm", emoji: "🥪"},
      {value: "evening", title: "After work", detail: "7:00 pm", emoji: "🌆"},
      {value: "night", title: "Before bed", detail: "9:00 pm", emoji: "🌙"}
    ]
  },
  {
    id: "source",
    title: "How did you hear about Sprout?",
    detail: "Helps us reach more people like you.",
    optional: true,
    options: [
      {value: "app-store", title: "App Store", emoji: "🔎"},
      {value: "tiktok-youtube", title: "TikTok or YouTube", emoji: "🎬"},
      {value: "meta", title: "Instagram or Facebook", emoji: "📸"},
      {value: "friend", title: "A friend", emoji: "🫶"},
      {value: "ai", title: "ChatGPT or another AI", emoji: "🤖"},
      {value: "search", title: "Google", emoji: "🌐"},
      {value: "reddit", title: "Reddit or a forum", emoji: "💬"},
      {value: "other", title: "Somewhere else", emoji: "✨"}
    ]
  }
];

export const personalityIds = ["foodie", "weekend_splurger", "subscription_collector", "silent_saver", "one_click", "ostrich", "planner", "dreamer"] as const;
export type PersonalityId = (typeof personalityIds)[number];

export interface Personality {
  id: PersonalityId;
  title: string;
  emoji: string;
  tagline: string;
  description: string;
  strengths: [string, string];
  watchOuts: [string, string];
  /** Share-card gradient, top to bottom. */
  colors: [string, string];
}

export const personalities: Readonly<Record<PersonalityId, Personality>> = {
  foodie: {
    id: "foodie",
    title: "The Foodie",
    emoji: "🍜",
    tagline: "Your love language is takeout.",
    description: "Good food is non-negotiable, and delivery apps know you by name. Sprout shows you what that habit really costs, so every meal still feels worth it.",
    strengths: ["You spend on what you love", "You know a good deal on a menu"],
    watchOuts: ["Small orders add up fast", "Delivery fees hide in plain sight"],
    colors: ["#ff8a3d", "#c2185b"]
  },
  weekend_splurger: {
    id: "weekend_splurger",
    title: "The Weekend Splurger",
    emoji: "🛍️",
    tagline: "Monday-you budgets. Saturday-you doesn’t.",
    description: "Weekdays are disciplined, weekends are a celebration. Sprout spots the Friday-to-Sunday spike so you can keep the fun and lose the regret.",
    strengths: ["Disciplined most of the week", "You make memories, not just purchases"],
    watchOuts: ["Weekend spikes erase weekday wins", "Bar tabs and brunches blur together"],
    colors: ["#7c4dff", "#e040fb"]
  },
  subscription_collector: {
    id: "subscription_collector",
    title: "The Subscription Collector",
    emoji: "📺",
    tagline: "A streaming service for every mood.",
    description: "Free trials have a way of becoming forever. Sprout finds every recurring charge hiding in your accounts, including the ones you forgot you had.",
    strengths: ["You try new things", "You love convenience"],
    watchOuts: ["Forgotten renewals", "Price increases you never noticed"],
    colors: ["#2979ff", "#00bfa5"]
  },
  silent_saver: {
    id: "silent_saver",
    title: "The Silent Saver",
    emoji: "🐿️",
    tagline: "Quietly stacking acorns.",
    description: "You already do a lot right, without much fanfare. Sprout helps you see the progress, so saving feels as good as spending.",
    strengths: ["Steady habits", "You think before you buy"],
    watchOuts: ["Money sitting idle", "Not celebrating your wins"],
    colors: ["#00c853", "#1b5e20"]
  },
  one_click: {
    id: "one_click",
    title: "The One-Click Wonder",
    emoji: "📦",
    tagline: "Add to cart is your cardio.",
    description: "Packages show up and you’re not always sure who ordered them. Sprout shows the real total behind all those little taps.",
    strengths: ["Decisive", "You find what you need fast"],
    watchOuts: ["Impulse buys", "Returns you never send"],
    colors: ["#ffb300", "#f4511e"]
  },
  ostrich: {
    id: "ostrich",
    title: "The Ostrich",
    emoji: "🙈",
    tagline: "If you don’t look, it didn’t happen.",
    description: "Checking your balance feels like opening a scary email. Sprout makes looking painless: one minute a day, in plain language, no judgment.",
    strengths: ["Calm under pressure", "Big-picture thinker"],
    watchOuts: ["Surprises at month end", "Fees that slip by unnoticed"],
    colors: ["#ec407a", "#7b1fa2"]
  },
  planner: {
    id: "planner",
    title: "The Spreadsheet Wizard",
    emoji: "📊",
    tagline: "You have a tab for your tabs.",
    description: "You already track everything. Sprout does the updating for you, so you get the insight without the data entry.",
    strengths: ["Detail oriented", "You know your numbers"],
    watchOuts: ["Time spent updating sheets", "Missing what the totals hide"],
    colors: ["#00acc1", "#283593"]
  },
  dreamer: {
    id: "dreamer",
    title: "The Big Dreamer",
    emoji: "🚀",
    tagline: "Eyes on the prize, wallet on the move.",
    description: "You know what you want your money to do. Sprout shows you where it goes today, so you can steer it toward the dream.",
    strengths: ["Clear goals", "Motivated by the future"],
    watchOuts: ["Daily leaks slowing big plans", "Optimism about next month"],
    colors: ["#651fff", "#00b0ff"]
  }
};

export interface PersonalityTrait {
  label: string;
  value: number;
}

/** "The Foodie" → "Foodie", for copy such as "Your Foodie plan". */
export function personalityShortName(personality: Pick<Personality, "title">): string {
  return personality.title.replace(/^The /, "");
}

/** Deterministic mapping from quiz answers to a personality and four 0–100 traits. */
export function personalityFromAnswers(answers: FunnelAnswers): {personality: Personality; traits: PersonalityTrait[]} {
  const id: PersonalityId =
    answers.feeling === "avoid"
      ? "ostrich"
      : answers.leak === "subscriptions" || answers.subscriptions === "7_10" || answers.subscriptions === "10_plus"
        ? "subscription_collector"
        : answers.leak === "food"
          ? "foodie"
          : answers.leak === "shopping"
            ? "one_click"
            : answers.leak === "fun"
              ? "weekend_splurger"
              : answers.checkFrequency === "daily" && answers.feeling === "confident"
                ? "planner"
                : answers.goal === "saving" && (answers.feeling === "okay" || answers.feeling === "confident")
                  ? "silent_saver"
                  : answers.feeling === "anxious" && answers.checkFrequency === "rarely"
                    ? "ostrich"
                    : "dreamer";
  const awareness = {daily: 82, weekly: 64, monthly: 41, rarely: 22}[answers.checkFrequency ?? "monthly"];
  const impulse = {food: 71, shopping: 86, subscriptions: 58, transport: 42, fun: 77, no_idea: 63}[answers.leak ?? "no_idea"];
  const calm = {anxious: 28, avoid: 21, okay: 57, confident: 81}[answers.feeling ?? "okay"];
  const ambition = {travel: 74, safety: 61, debt: 69, invest: 83, home: 78, freedom: 88}[answers.dream ?? "freedom"];
  return {
    personality: personalities[id],
    traits: [
      {label: "Spending impulse", value: impulse},
      {label: "Money awareness", value: awareness},
      {label: "Money calm", value: calm},
      {label: "Big-goal energy", value: ambition}
    ]
  };
}

/** Midpoint of the monthly spending guess, in USD minor units, for the guess-vs-reality reveal. */
export function spendGuessMinor(guess: FunnelAnswers["spendGuess"]): number | null {
  if (!guess) return null;
  return {under_1k: 75_000, "1k_2k": 150_000, "2k_4k": 300_000, "4k_6k": 500_000, over_6k: 750_000}[guess];
}

export function spendGuessLabel(guess: FunnelAnswers["spendGuess"]): string | null {
  if (!guess) return null;
  return {under_1k: "under $1,000", "1k_2k": "$1,000–$2,000", "2k_4k": "$2,000–$4,000", "4k_6k": "$4,000–$6,000", over_6k: "over $6,000"}[guess];
}

/** Daily check-in reminder hour chosen in the quiz. */
export function recapHourFor(time: FunnelAnswers["checkInTime"]): number {
  return {morning: 8, midday: 12, evening: 19, night: 21}[time ?? "evening"];
}

export const dreamLabels: Readonly<Record<NonNullable<FunnelAnswers["dream"]>, string>> = {
  travel: "a dream trip",
  safety: "a safety net",
  debt: "being debt-free",
  invest: "investing more",
  home: "a home of your own",
  freedom: "more freedom"
};
